import React, { useEffect, useRef, useState } from "react";
import { Mail, Loader, RotateCcw } from "lucide-react";

interface Props {
  email: string;
  onSubmit: (otp: string) => Promise<void> | void;
  onResend: () => Promise<void> | void;
  onChangeEmail: () => void;
  submitting?: boolean;
  resending?: boolean;
  length?: number;
}

const OtpCheckEmailCard: React.FC<Props> = ({
  email,
  onSubmit,
  onResend,
  onChangeEmail,
  submitting = false,
  resending = false,
  length = 6,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(length).fill(""));
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [focused, setFocused] = useState<number | null>(0);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (digits.every((d) => d !== "") && !submitting) {
      void onSubmit(digits.join(""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  const setAt = (i: number, v: string) => {
    setDigits((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  };

  const handleChange = (i: number, raw: string) => {
    const v = raw.replace(/\D/g, "");
    if (!v) {
      setAt(i, "");
      return;
    }
    if (v.length === 1) {
      setAt(i, v);
      if (i < length - 1) refs.current[i + 1]?.focus();
    } else {
      // pasted multi-digit
      const chars = v.slice(0, length - i).split("");
      setDigits((prev) => {
        const next = [...prev];
        chars.forEach((c, idx) => (next[i + idx] = c));
        return next;
      });
      const nextIdx = Math.min(i + chars.length, length - 1);
      refs.current[nextIdx]?.focus();
    }
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
      setAt(i - 1, "");
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!text) return;
    e.preventDefault();
    const next = Array(length).fill("");
    text.split("").forEach((c, idx) => (next[idx] = c));
    setDigits(next);
    refs.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-8 py-8 max-w-md w-full mx-auto">
      <div className="flex flex-col items-center text-center space-y-1.5">
        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
          <Mail className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mt-2">Check your email</h2>
        <p className="text-sm text-gray-500">We sent a 6-digit code to</p>
        <p className="text-sm font-semibold text-gray-900 break-all">{email}</p>
      </div>

      <div className="flex justify-between gap-2 mt-6">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={d}
            disabled={submitting}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={() => setFocused(i)}
            onBlur={() => setFocused((cur) => (cur === i ? null : cur))}
            onPaste={handlePaste}
            className={`w-12 h-14 text-center text-xl font-bold rounded-lg bg-white transition
              ${d ? "border-2 border-gray-300 text-gray-900" : "border border-gray-200 text-gray-400"}
              ${focused === i ? "border-2 border-blue-600 ring-2 ring-blue-100" : ""}
              focus:outline-none disabled:opacity-50`}
          />
        ))}
      </div>

      <div className="flex items-center justify-between mt-6 text-sm">
        <button
          type="button"
          onClick={onChangeEmail}
          disabled={submitting}
          className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-50"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Change email
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={submitting || resending}
          className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-semibold disabled:opacity-50"
        >
          {resending && <Loader className="h-3.5 w-3.5 animate-spin" />}
          Resend code
        </button>
      </div>

      {submitting && (
        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
          <Loader className="h-3 w-3 animate-spin" />
          Verifying…
        </div>
      )}
    </div>
  );
};

export default OtpCheckEmailCard;
